import {
  CustomerAddress,
  CustomerAddressCity,
  CustomerAddressArea,
  CustomerAddressPosition,
  CustomerAddressAvenue,
  CustomerAddressBlock,
} from "@/types/customer/address.type";

import CountryCodeLocations from "@/config/iso_code_positions";
import { CustomerPosition } from "./position";
import { StorageConstants } from "@/enums/storage.enum";
import Storage from "@/services/storage";
import _ from "lodash";
import { AppLang } from "@/types/lang";

export class CustomerAddressModule implements CustomerAddressInterface {
  id: any;
  label: string = "";
  residence: string = "";
  street: string = "";
  building: string = "";
  position: CustomerAddressPosition;
  is_primary: boolean = false;
  is_verified: boolean = false;
  city: CustomerAddressCity;
  area: CustomerAddressArea;
  floor: string | null = null;
  apartment: string | null = null;
  extras?: any;
  avenue?: CustomerAddressAvenue;
  block?: CustomerAddressBlock;
  avenue_id?: string = "";
  block_id?: string = "";
  area_id: string = "";
  city_id: string = "";
  details: string = "";
  selected?: boolean = false;
  country_iso_code: string = "";
  lang: string = AppLang.EN;

  constructor(address: CustomerAddress) {
    this.country_iso_code = Storage.get(StorageConstants.BUSINESS).country;
    const business_position = CountryCodeLocations[this.country_iso_code.toLowerCase()];
    this.id = address?.id || null;
    this.label = address?.label || "";
    this.residence = address?.residence || "";
    this.street = address?.street || "";
    this.building = address?.building || "";
    this.floor = address?.floor || "";
    this.apartment = address?.apartment || "";
    this.extras = address?.extras || "";
    this.is_primary = address?.is_primary || false;
    this.selected = address?.is_primary || false;
    this.is_verified = address?.is_verified || false;
    this.city = address?.city;
    this.area = address?.area;
    this.avenue = address?.avenue;
    this.block = address?.block;
    this.area_id = address?.area?.id || address?.area_id || "";
    this.city_id = address?.city?.id || address?.city_id || "";
    this.avenue_id = address?.avenue?.id || address?.avenue_id || "";
    this.block_id = address?.block?.id || address?.block_id || "";

    this.position = new CustomerPosition({
      latitude: address?.position?.latitude || business_position.lat,
      longitude: address?.position?.longitude || business_position.long,
    });

    this.details = this.getDetails();
    this.lang = Storage.get(StorageConstants.LANG) || AppLang.EN;
  }

  getAddress(): any {
    return {
      id: this.id,
      label: this.label,
      residence: this.residence,
      street: this.street,
      building: this.building,
      position: new CustomerPosition({
        latitude: this.position.latitude,
        longitude: this.position.longitude,
      }),
      is_primary: this.is_primary,
      is_verified: this.is_verified,
      city: this.city,
      area: this.area,
      floor: this.floor,
      apartment: this.apartment,
      extras: this.extras,
      avenue: this.getAvenueObject(),
      block: this.getBlockObject(),
      avenue_id: this.avenue_id || this?.avenue?.id,
      block_id: this.block_id || this?.block?.id,
      city_id: this.city_id,
      area_id: this.area_id,
    };
  }

  getAvenueObject = () => {
    return {
      en: this.avenue?.en,
      ar: this.avenue?.ar,
      id: this.avenue?.id,
    };
  };

  getBlockObject = () => {
    return {
      en: this.block?.en,
      ar: this.block?.ar,
      id: this.block?.id,
    };
  };

  /***
   * get address details
   * @return {void}
   */
  private getDetails(): string {
    let text = "";
    if (_.isObject(this.city)) {
      text = this.city["en"];
    }

    if (_.isObject(this.area)) {
      text = `${text} - ${this.area["en"]}`;
    }

    if (!_.isEmpty(this.residence)) {
      text = `${text} - ${this.residence}`;
    }

    if (!_.isEmpty(this?.street)) {
      text = `${text} - ${this.street}`;
    }

    if (!_.isEmpty(this?.floor)) {
      text = `${text} - ${this.floor}`;
    }

    if (!_.isEmpty(this?.apartment)) {
      text = `${text} - ${this.apartment}`;
    }

    return text;
  }

  /***
   * handle select address
   * @return {void}
   */
  public select(): void {
    this.selected = true;
  }

  /***
   * handle unSelect address
   * @return {void}
   */
  public unSelect(): void {
    this.selected = false;
  }

  public getAddressString() {
    const address: Array<string> = [];
    address.push(this.country_iso_code);

    if (this.city) {
      address.push(this.city?.ar);
    }

    if (this.area) {
      address.push(this.area?.ar);
    }

    if (this.street) {
      address.push(this.street);
    }

    if (this.building) {
      address.push(this.building);
    }

    // console.log("address", address.join(" - "));
    return address.join(" - ");
  }

  /**
   * Get Address City name by current lang
   * @return {string}
   */
  public getCityName(): string {
    return this.city?.[`${this.lang}`] || this.city?.[`name_${this.lang}`];
  }

  /**
   * Get Address Area name by current lang
   * @return {string}
   */
  public getAreaName(): string {
    return this.area?.[`${this.lang}`] || this.area?.[`name_${this.lang}`];
  }

  /**
   * Get Address Avenue name by current lang
   * @return {string}
   */
  public getAvenue(): string {
    if (!this.avenue) {
      return "";
    }

    return this.avenue?.[`${this.lang}`] || this.area?.[`name_${this.lang}`];
  }

  /**
   * Get Address block name by current lang
   * @return {string}
   */
  public getBlock(): string {
    if (!this.block) {
      return "";
    }

    return this.block?.[`${this.lang}`] || this.area?.[`name_${this.lang}`];
  }

  public setAddressJson = () => {
    if (this.city) {
      this.city = {
        en: this.city.en,
        ar: this.city.ar,
        id: this.city.id,
      };
    }

    if (this.area) {
      this.area = {
        en: this.area.en,
        ar: this.area.ar,
        id: this.area.id,
      };
    }

    if (this.avenue) {
      this.avenue = {
        en: this.avenue.en,
        ar: this.avenue.ar,
        id: this.avenue.id,
      };
    }

    if (this.block) {
      this.block = {
        en: this.block.en,
        ar: this.block.ar,
        id: this.block.id,
      };
    }
  };
}
